import { useEffect } from 'react';

/** Match `index.css` mobile rules — virtual keyboard / small screens */
const MOBILE_MEDIA_QUERY = '(max-width: 768px)';

function isTextLikeEditable(el: EventTarget | null): el is HTMLElement {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  if (el instanceof HTMLTextAreaElement) return true;
  if (el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) {
    const t = el.type;
    if (
      t === 'hidden' ||
      t === 'button' ||
      t === 'submit' ||
      t === 'reset' ||
      t === 'checkbox' ||
      t === 'radio' ||
      t === 'file' ||
      t === 'image'
    ) {
      return false;
    }
    return true;
  }
  return false;
}

function scrollIntoViewCentered(el: HTMLElement, behavior: ScrollBehavior) {
  el.scrollIntoView({
    block: 'center',
    inline: 'nearest',
    behavior,
  });
}

/**
 * Mobile browsers often defer layout until the first input event, so the caret stays
 * off-screen until a key is pressed. Scroll the focused field into view on focus and
 * again after the keyboard animation (visualViewport) so the cursor is visible immediately.
 */
export function useMobileFocusScrollIntoView() {
  useEffect(() => {
    const isMobile = () => window.matchMedia(MOBILE_MEDIA_QUERY).matches;

    const scheduleScroll = (el: HTMLElement) => {
      // Immediate scroll — no wait for first keypress
      requestAnimationFrame(() => {
        scrollIntoViewCentered(el, 'auto');
        requestAnimationFrame(() => {
          scrollIntoViewCentered(el, 'auto');
          // Keyboard / URL bar animation finishes at different times on iOS vs Android
          window.setTimeout(() => scrollIntoViewCentered(el, 'smooth'), 80);
          window.setTimeout(() => scrollIntoViewCentered(el, 'smooth'), 200);
          window.setTimeout(() => scrollIntoViewCentered(el, 'smooth'), 400);
        });
      });
    };

    const onFocusIn = (e: FocusEvent) => {
      if (!isMobile()) return;
      const target = e.target;
      if (!isTextLikeEditable(target)) return;
      scheduleScroll(target);
    };

    let lastVvHeight = window.visualViewport?.height ?? window.innerHeight;

    const onVisualViewportResize = () => {
      if (!isMobile()) return;
      const vv = window.visualViewport;
      if (!vv) return;
      const h = vv.height;
      // Keyboard opened (viewport shrank)
      if (h < lastVvHeight - 48) {
        const active = document.activeElement;
        if (active && isTextLikeEditable(active)) {
          scheduleScroll(active);
        }
      }
      lastVvHeight = h;
    };

    document.addEventListener('focusin', onFocusIn, true);
    window.visualViewport?.addEventListener('resize', onVisualViewportResize);

    return () => {
      document.removeEventListener('focusin', onFocusIn, true);
      window.visualViewport?.removeEventListener('resize', onVisualViewportResize);
    };
  }, []);
}
